import type { CampaignSimpleResponse, PopupResponse } from '@coorum-research/shared-common/api-response/popup.schema'
import type { ThanksLinkType } from '../utils/user-action-history.util'
import { ThanksComponent } from '@coorum-research/core/src/components/ThanksComponent'
import { useOnceCall } from '@coorum-research/core/src/composables/use-once-call'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useQuery } from '@tanstack/react-query'
import { thanksContentApi } from '../apis/launcher-contents.api'
import { mypageSettingApi } from '../apis/mypage-settings.api'
import { recordUserActionHistory } from '../utils/user-action-history.util'
import { featureFlags } from './feature-flag/feature-flag.config'

type Props = {
  popup?: PopupResponse
  campaign?: CampaignSimpleResponse
  launcherContentId?: string | null
  onClickButton?: () => void
  openedFrom: 'create' | 'update'
}

/**
 * 渡されたlauncherContentIdを元に、ThanksComponentを表示する
 */
export function ThanksPage({
  popup,
  campaign,
  launcherContentId,
  onClickButton,
  openedFrom,
}: Props) {
  const { getThanksContent } = thanksContentApi()
  const { getMyPageSetting } = mypageSettingApi()

  const { data: launcherContent, isFetching: isThanksPageFetching } = useQuery({
    queryKey: ['get-thanks-content', launcherContentId],
    queryFn: () => {
      return getThanksContent(launcherContentId ?? '')
    },
    refetchOnWindowFocus: false,
    enabled: !!launcherContentId,
  })

  const { data: mypageSetting, isFetching: isMypageSettingFetching } = useQuery({
    queryKey: ['get-mypage-setting'],
    queryFn: () => getMyPageSetting(),
    refetchOnWindowFocus: false,
  })

  const isUserActionHistoriesEnabled = useBooleanFlagValue(featureFlags.ACCOUNT_USER_ACTION_HISTORIES, false)

  // データ計測（thanksView)
  useOnceCall(() => {
    if (isUserActionHistoriesEnabled && openedFrom === 'create') {
      recordUserActionHistory({
        actionType: 'thanksView',
        popupId: popup?.id,
        popupName: popup?.name,
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        surveyId: campaign?.surveyId ?? undefined,
        surveyName: campaign?.surveyName ?? undefined,
        launcherContentId: launcherContentId ?? undefined,
        launcherContentName: launcherContent?.name,
        // }).then(() => {
        //   console.log('recordUserActionHistory thanksView success')
      }).catch((error) => {
        console.error('recordUserActionHistory thanksView error', error)
      })
    }
  }, !isThanksPageFetching)

  const handleThanksLinkClick = (thanksLinkType?: ThanksLinkType, thanksLinkUrl?: string) => {
    // データ計測（thanksLinkClick)
    if (isUserActionHistoriesEnabled && openedFrom === 'create') {
      recordUserActionHistory({
        actionType: 'thanksLinkClick',
        popupId: popup?.id,
        popupName: popup?.name,
        campaignId: campaign?.id,
        campaignName: campaign?.name,
        surveyId: campaign?.surveyId ?? undefined,
        surveyName: campaign?.surveyName ?? undefined,
        launcherContentId: launcherContentId ?? undefined,
        launcherContentName: launcherContent?.name,
        thanksLinkType,
        thanksLinkUrl,
      }).then(() => {
        // console.log('recordUserActionHistory thanksView success')
        window.open(thanksLinkUrl, '_blank', 'noopener')
      }).catch((error) => {
        console.error('recordUserActionHistory thanksView error', error)
        // エラー時にも一旦開く
        window.open(thanksLinkUrl, '_blank', 'noopener')
      })
    } else {
      // データ計測機能が開放されていないアカウントの場合は、普通にリンクを開く
      window.open(thanksLinkUrl, '_blank', 'noopener')
    }
  }

  return (
    <>
      {!isThanksPageFetching && !isMypageSettingFetching && (
        <ThanksComponent
          content={launcherContent?.configJson ?? undefined}
          onClickThanksLink={(thanksLinkType, thanksLinkUrl) => handleThanksLinkClick(thanksLinkType, thanksLinkUrl)}
          onClick={onClickButton}
          isMypageVisible={!!mypageSetting}
        />
      )}
    </>
  )
}
